import {
  ClipboardCheck,
  LayoutDashboard,
  GraduationCap,
  Users,
  UserCog,
  CalendarCheck,
  BookOpen,
  Clock3,
  ClipboardList,
  Wallet,
  Megaphone,
  MessageSquare,
  FileText,
  Library,
  Bus,
  Home as HomeIcon,
  Coffee,
  ShoppingCart,
  Wrench,
  Banknote,
  BarChart3,
  Smartphone,
  Award,
  Shield,
  Building2,
  Layers,
  ScanEye,
  Landmark,
  Rows3,
  Plane,
} from "lucide-react";

// Sprint-1 visible (live) routes have `enabled: true`. Everything else is
// rendered with a "Sprint N" badge to convey the full scale of the platform.
// Groups with `flat: true` render as top-level items without a section header.
export const MODULE_GROUPS = [
  {
    label: "Top",
    flat: true,
    items: [
      { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/dashboard", enabled: true },
      { key: "tenants", label: "Tenants", icon: Building2, path: "/tenants", enabled: true, requireRoles: ["super_admin"] },
      { key: "institutions", label: "Institutions", icon: GraduationCap, path: "/institutions", enabled: true },
    ],
  },
  {
    label: "Campus",
    items: [
      { key: "campuses", label: "Campuses", icon: Building2, path: "/campuses", enabled: true },
      { key: "academic", label: "Academic setup", icon: BookOpen, path: "/academic", enabled: true },
      { key: "bulk-import", label: "Bulk import", icon: FileText, path: "/bulk-import", enabled: true },
      { key: "bulk-onboard", label: "Bulk onboarding", icon: Rows3, path: "/bulk-onboard", enabled: true, requireRoles: ["super_admin"] },
      { key: "users", label: "Roles & Permissions", icon: Shield, path: "/users", enabled: true, requireRoles: ["super_admin", "inst_admin", "campus_admin"] },
      { key: "role-templates", label: "Role templates", icon: Shield, path: "/role-templates", enabled: true },
      { key: "students", label: "Students", icon: GraduationCap, path: "/students", enabled: true },
      { key: "parents", label: "Parents", icon: Users, path: "/parents", enabled: true },
      { key: "staff", label: "Staff", icon: UserCog, path: "/staff", enabled: true },
    ],
  },
  {
    label: "360 Views",
    items: [
      { key: "campus-360", label: "Campus 360°", icon: ScanEye, path: "/campus-360", enabled: true, requireRoles: ["super_admin", "inst_admin", "campus_admin"] },
      { key: "staff-360", label: "Staff 360°", icon: ScanEye, path: "/staff-360", enabled: true, requireRoles: ["super_admin", "inst_admin", "campus_admin"] },
      { key: "student-360", label: "Student 360°", icon: ScanEye, path: "/student-360", enabled: true },
      { key: "department-360", label: "Department 360°", icon: ScanEye, path: "/department-360", enabled: true, requireRoles: ["super_admin", "inst_admin", "campus_admin"] },
      { key: "finance-360", label: "Finance 360°", icon: ScanEye, path: "/finance-360", enabled: true, requireRoles: ["super_admin", "inst_admin", "campus_admin", "finance", "accountant"] },
    ],
  },
  {
    label: "Academic Operations",
    items: [
      { key: "admissions", label: "Admissions", icon: FileText, path: "/admissions", enabled: true },
      { key: "attendance", label: "Attendance", icon: CalendarCheck, path: "/attendance", enabled: true },
      { key: "staff-attendance", label: "Staff Attendance", icon: ClipboardCheck, path: "/staff-attendance", enabled: true, requireRoles: ["super_admin", "inst_admin", "campus_admin", "principal", "hr", "teacher"] },
      { key: "leaves", label: "Leaves", icon: Plane, path: "/leaves", enabled: true, requireRoles: ["super_admin", "inst_admin", "campus_admin", "principal", "hr", "teacher"] },
      { key: "regularisations", label: "Regularisations", icon: ClipboardList, path: "/regularisations", enabled: true },
      { key: "fees", label: "Fees & Payments", icon: Wallet, path: "/fees", enabled: true },
      { key: "notices", label: "Notice Board", icon: Megaphone, path: "/notices", enabled: true },
      { key: "homework", label: "Homework", icon: ClipboardList, path: "/homework", enabled: true },
      { key: "library", label: "Library", icon: Library, path: "/library", enabled: true },
    ],
  },
  {
    label: "Academics & Intelligence",
    items: [
      { key: "classes-sections", label: "Classes & Sections", icon: Layers, path: "/classes-sections", enabled: true },
      { key: "syllabus", label: "Syllabus", icon: FileText, path: "/syllabus", enabled: true },
      { key: "curriculum", label: "Curriculum", icon: BookOpen, path: "/curriculum", enabled: true },
      { key: "exam-structure", label: "Exam Structure", icon: ClipboardCheck, path: "/exam-structure", enabled: true },
      { key: "gradebook", label: "Gradebook", icon: Award, path: "/gradebook", enabled: true },
      { key: "timetable", label: "Timetable & Exams", icon: Clock3, path: "/timetable", enabled: true },
      { key: "exam-sessions", label: "Exam Sessions", icon: ScanEye, path: "/exam-sessions", enabled: true, requireRoles: ["super_admin", "inst_admin", "principal", "campus_admin", "hod", "coordinator", "exam_controller"] },
      { key: "infrastructure", label: "Infrastructure", icon: Landmark, path: "/infrastructure", enabled: true, requireRoles: ["super_admin", "inst_admin", "principal", "campus_admin", "infrastructure_manager", "it_admin"] },
      { key: "transport", label: "Transport", icon: Bus, path: "/transport", sprint: 13 },
      { key: "hostel", label: "Hostel & Mess", icon: HomeIcon, path: "/hostel", sprint: 14 },
      { key: "hr", label: "HR & Payroll", icon: UserCog, path: "/hr", sprint: 15 },
    ],
  },
  {
    label: "Operations",
    items: [
      { key: "procurement", label: "Procurement", icon: ShoppingCart, path: "/procurement", sprint: 16 },
      { key: "maintenance", label: "Maintenance & Canteen", icon: Wrench, path: "/maintenance", sprint: 17 },
      { key: "finance", label: "Accounting & Finance", icon: Banknote, path: "/finance", sprint: 18 },
      { key: "analytics", label: "Analytics & BI", icon: BarChart3, path: "/analytics", sprint: 19 },
      { key: "mobile", label: "Mobile App", icon: Smartphone, path: "/mobile", sprint: 21 },
    ],
  },
  {
    label: "Enterprise",
    items: [
      { key: "accreditation", label: "Accreditation", icon: Award, path: "/accreditation", sprint: 22 },
      { key: "scale", label: "SSO & Scale", icon: Shield, path: "/scale", sprint: 24 },
    ],
  },
];

// Role visibility — show every module in the sidebar for every role.
// Backend RBAC still gates actual writes/reads; the sidebar is just a directory.
export const ROLE_VISIBILITY = {
  super_admin: "*",
  tenant_admin: "*",
  inst_admin: "*",
  campus_admin: "*",
  principal: "*",
  hod: "*",
  teacher: "*",
  class_teacher: "*",
  accountant: "*",
  librarian: "*",
  student: "*",
  parent: "*",
};
